import {
    formatTicketClientDisplay,
    getTicketAssigneeServiceGroupId,
    getTicketClosingDate,
    getTicketClientNameLabel,
    getTicketClientRegistration,
    getTicketClientRepresentativeType,
    getTicketStatusExportLabel,
} from './ticketAdvancedSearchFilters';
import { getTicketAssigneeDisplayName } from './ticketAssignee';

const MANDATORY_COVERED_STANDARD_KEYS = new Set([
    'id',
    'title',
    'status',
    'priority',
    'assigned_to',
    'category',
    'is_internal',
    'created_at',
    'client',
    // O Canal passou a ser coluna obrigatória (pedido da Gisele, ponto 13).
    // Sem entrar aqui, um filtro que também o marque como campo visível o
    // acrescentaria de novo -- o dedup por cabeçalho já barraria, mas
    // declarar a chave deixa a intenção explícita em vez de depender do
    // acaso de os dois textos coincidirem.
    'channel',
]);

const formatExportDateTime = (value) => {
    if (value == null || value === '') return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('pt-BR');
};

const stripHtmlForExport = (html) => {
    const raw = String(html || '');
    return raw
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

const getTicketServiceExportLabels = (ticket) => {
    const svc = ticket?.service;
    if (!svc) return { full: '', subgroup: '' };

    const parent = svc.parent ?? svc.parent_service ?? null;
    if (svc.parent_id != null) {
        return {
            full: (parent?.name || '').trim(),
            subgroup: (svc.name || '').trim(),
        };
    }

    return {
        full: (svc.name || '').trim(),
        subgroup: '',
    };
};

/**
 * Limite de caracteres de UMA CÉLULA do Excel.
 *
 * A biblioteca `xlsx` LANÇA ao passar disso ("Text length must not exceed
 * 32767 characters"), e os dois handlers de exportação aguardam o resultado
 * sem `try/catch` -- então a promise rejeitava em silêncio e NADA acontecia:
 * sem arquivo, sem erro, sem aviso.
 *
 * Isso não era visível enquanto a coluna da última ação saía vazia. Passou a
 * ser no instante em que ela começou a trazer o conteúdo de verdade: a maior
 * ação do banco local tem 33.618 caracteres, porque a ação de um chamado
 * vindo de chat é a transcrição INTEIRA da conversa.
 */
const LIMITE_DA_CELULA = 32767;
const MARCA_DE_CORTE = '... [texto cortado para caber na planilha]';

/**
 * A ação de um chamado nascido de chat não é texto: é o JSON da transcrição
 * inteira, do formato `{ chat_info: {...}, messages: [...] }`.
 *
 * A tela do chamado parseia esse JSON e desenha a conversa formatada
 * (`components/ChatTranscription`). A exportação despejava o JSON cru na
 * célula -- chaves, aspas escapadas e os emojis de `sender_icon` --, o que é
 * ilegível numa planilha.
 *
 * Devolve `null` quando não é uma transcrição, e aí o texto segue o caminho
 * normal. O teste é pela FORMA (tem `chat_info` e `messages`), não só por ser
 * JSON válido: outro JSON qualquer gravado como ação continua saindo como
 * está, em vez de virar um resumo errado.
 */
const resumirTranscricao = (texto) => {
    const cru = (texto || '').trim();
    if (!cru.startsWith('{')) return null;

    let dados;
    try {
        dados = JSON.parse(cru);
    } catch (e) {
        return null;
    }

    if (!dados || typeof dados !== 'object') return null;
    if (!dados.chat_info || !Array.isArray(dados.messages)) return null;

    const mensagens = dados.messages;
    const total = dados.chat_info.total_messages ?? mensagens.length;
    const inicio = dados.chat_info.start_date;
    const ultima = mensagens.length ? mensagens[mensagens.length - 1] : null;

    const partes = [`Conversa de chat — ${total} mensagem(ns)`];
    if (inicio) partes.push(`iniciada em ${inicio}`);

    let resumo = partes.join(', ') + '.';

    if (ultima) {
        // O conteúdo vem com quebras de linha ("Atualizou\n\nAtendimento
        // encerrado por: ..."). Numa célula de planilha elas viram ruído.
        const conteudo = String(ultima.content || '').replace(/\s+/g, ' ').trim();
        const quem = String(ultima.sender_name || '').trim();

        if (conteudo || quem) {
            // Sem o `sender_icon`: ele é emoji, e emoji não entra na planilha
            // pelo mesmo motivo que não entra na interface.
            resumo += ` Última (${ultima.timestamp || 's/ data'}) ${quem}: ${conteudo}`;
        }
    }

    return resumo.trim();
};

const getTicketLatestActionText = (ticket) => {
    const comment = ticket?.latest_comment ?? ticket?.latestComment ?? null;
    if (!comment) return '';

    const resumoDoChat = resumirTranscricao(comment.comment ?? comment.text ?? '');
    if (resumoDoChat !== null) return resumoDoChat;

    const texto = stripHtmlForExport(comment.comment ?? comment.text ?? '');

    if (texto.length <= LIMITE_DA_CELULA) return texto;

    // O corte vale para TODOS os formatos, não só o XLSX. O CSV aguentaria o
    // texto inteiro, mas uma planilha que muda de conteúdo conforme o formato
    // escolhido é pior que uma que corta sempre no mesmo ponto -- e quem
    // compara as duas conclui que uma delas perdeu dado sem avisar.
    return texto.slice(0, LIMITE_DA_CELULA - MARCA_DE_CORTE.length) + MARCA_DE_CORTE;
};

const getStandardExportFieldDefinitions = ({ mapStatus, mapPriority }) => ({
    id: { header: 'Protocolo', getter: (t) => t.id ?? '' },
    title: { header: 'Assunto', getter: (t) => t.title || '' },
    status: { header: 'Status', getter: (t) => getTicketStatusExportLabel(t, mapStatus) },
    priority: { header: 'Prioridade', getter: (t) => mapPriority(t.priority) },
    assigned_to: { header: 'Responsável', getter: (t) => getTicketAssigneeDisplayName(t) },
    service_group_id: {
        header: 'Grupo (responsável)',
        getter: (t) =>
            t.assigned_to?.service_group?.description ||
            (getTicketAssigneeServiceGroupId(t) != null ? `#${getTicketAssigneeServiceGroupId(t)}` : ''),
    },
    created_by: { header: 'Criado por', getter: (t) => t.created_by?.name || 'Sistema' },
    client: { header: 'Cliente', getter: (t) => formatTicketClientDisplay(t) || '' },
    category: { header: 'Categoria', getter: (t) => t.category || '' },
    contact: { header: 'Contato', getter: (t) => t.contact || '' },
    channel: { header: 'Canal', getter: (t) => t.channel || '' },
    is_internal: {
        header: 'Tipo',
        getter: (t) => (t.is_internal ? 'Interno' : 'Público'),
    },
    created_at: {
        header: 'Data de abertura',
        getter: (t) => formatExportDateTime(t.created_at),
    },
    resolved_at: {
        header: 'Data de resolução',
        getter: (t) => formatExportDateTime(t.resolved_at),
    },
    updated_at: {
        header: 'Data de Atualização',
        getter: (t) => formatExportDateTime(t.updated_at),
    },
});

const isFilterCustomFieldVisible = (field) => {
    if (field?.pivot != null) return Boolean(field.pivot.visible);
    if (field?.visible != null) return Boolean(field.visible);
    return true;
};

/** Colunas fixas para exportação analítica — sempre presentes. */
export const getTicketExportAnalysisColumns = ({ mapStatus, mapPriority }) => [
    { key: 'ticket_number', header: 'Número do ticket', getter: (t) => t.id ?? '' },
    {
        key: 'type',
        header: 'Tipo',
        getter: (t) => (t.is_internal ? 'Interno' : 'Público'),
    },
    { key: 'title', header: 'Assunto', getter: (t) => t.title || '' },
    {
        key: 'service_full',
        header: 'Serviço Completo',
        getter: (t) => getTicketServiceExportLabels(t).full,
    },
    {
        key: 'service_subgroup',
        header: 'Serviço (subgrupo)',
        getter: (t) => getTicketServiceExportLabels(t).subgroup,
    },
    {
        key: 'created_at',
        header: 'Data Abertura',
        getter: (t) => formatExportDateTime(t.created_at),
    },
    {
        key: 'closing_date',
        header: 'Data Fechamento',
        getter: (t) => formatExportDateTime(getTicketClosingDate(t)),
    },
    {
        key: 'client_registration',
        header: 'Cliente (registro)',
        getter: (t) => getTicketClientRegistration(t),
    },
    {
        key: 'client_name',
        header: 'Cliente (nome)',
        getter: (t) => getTicketClientNameLabel(t),
    },
    {
        key: 'client_representative_type',
        header: 'Cliente (tipo representante)',
        getter: (t) => getTicketClientRepresentativeType(t),
    },
    {
        key: 'assigned_to',
        header: 'Responsável técnico',
        getter: (t) => getTicketAssigneeDisplayName(t),
    },
    { key: 'category', header: 'Categoria', getter: (t) => t.category || '' },
    {
        key: 'priority',
        header: 'Urgência',
        getter: (t) => mapPriority(t.priority),
    },
    {
        key: 'status',
        header: 'Status',
        getter: (t) => getTicketStatusExportLabel(t, mapStatus),
    },
    {
        // O Canal e a Última ação são os dois campos que a Gisele pediu no
        // ponto 13, e ficam lado a lado de propósito.
        //
        // Antes o Canal só saía quando o filtro selecionado tivesse
        // `standard_fields` com `channel` visível -- ou seja, exportar sem
        // filtro, ou com um filtro que não o marcasse, simplesmente não
        // trazia a coluna, e quem exportava não tinha como saber que ela
        // existia.
        //
        // Pode vir vazio nos chamados antigos: até 29/09 o chamado nascido
        // de chat não gravava canal nenhum. O comando
        // `alodesk:backfill-canal-do-chamado` preenche o histórico.
        key: 'channel',
        header: 'Canal',
        getter: (t) => t.channel || '',
    },
    {
        key: 'latest_action',
        header: 'Ultima ação do ticket',
        getter: (t) => getTicketLatestActionText(t),
    },
];

/** Colunas extras visíveis no filtro, excluindo as já cobertas pelas obrigatórias. */
export const getFilterExportExtraColumns = (selectedFilter, { mapStatus, mapPriority }) => {
    if (!selectedFilter || typeof selectedFilter !== 'object') return [];

    const mandatoryHeaders = new Set(
        getTicketExportAnalysisColumns({ mapStatus, mapPriority }).map((col) => col.header)
    );
    const usedKeys = new Set();
    const extra = [];

    const standardDefs = getStandardExportFieldDefinitions({ mapStatus, mapPriority });
    const standardFields =
        selectedFilter.settings?.standard_fields || selectedFilter.standard_fields || [];

    if (Array.isArray(standardFields)) {
        standardFields.forEach((field) => {
            if (!field?.visible || !field.name) return;
            if (MANDATORY_COVERED_STANDARD_KEYS.has(field.name)) return;

            const def = standardDefs[field.name];
            if (!def || usedKeys.has(field.name) || mandatoryHeaders.has(def.header)) return;

            usedKeys.add(field.name);
            mandatoryHeaders.add(def.header);
            extra.push({ key: field.name, header: def.header, getter: def.getter });
        });
    }

    const customFields = Array.isArray(selectedFilter.fields) ? selectedFilter.fields : [];
    customFields.forEach((field) => {
        if (!isFilterCustomFieldVisible(field)) return;

        const fieldId = field.id ?? field.field_id;
        if (fieldId == null) return;

        const key = `field_${fieldId}`;
        const header = (field.name || '').trim();
        if (!header || usedKeys.has(key) || mandatoryHeaders.has(header)) return;

        usedKeys.add(key);
        mandatoryHeaders.add(header);
        extra.push({
            key,
            header,
            getter: (t) => {
                const allFields = t.fields || [];
                const match = allFields.find((ff) => Number(ff.field_id) === Number(fieldId));
                return match != null ? match.value : '';
            },
        });
    });

    return extra;
};

/** Colunas obrigatórias + extras do filtro (sem duplicar). */
export const getTicketExportColumns = (selectedFilter, { mapStatus, mapPriority }) => [
    ...getTicketExportAnalysisColumns({ mapStatus, mapPriority }),
    ...getFilterExportExtraColumns(selectedFilter, { mapStatus, mapPriority }),
];

export const ticketToExportRow = (ticket, selectedFilter, { mapStatus, mapPriority }) => {
    const cols = getTicketExportColumns(selectedFilter, { mapStatus, mapPriority });
    const row = {};
    cols.forEach((col) => {
        row[col.header] = col.getter(ticket);
    });
    return row;
};